import React, { Component } from "react";
import { Button, Spin } from "antd";
import { Bar, Line } from "react-chartjs-2";
import moment from "moment";
import StatsUtils from "../../../utils/stats";
import { idToColor } from "../utils";
import "chartjs-plugin-datalabels";

const GraphSpinner = props => {
  const { loading } = props;
  if (!loading) {
    return null;
  }

  return <Spin tip={"Veuillez patienter, le temps de chargement peut prendre plusieurs secondes"} />;
};

const GraphWithView = ({ view, ...props }) => {
  switch (view) {
    case "bar":
      return <Bar {...props} />;
    case "line":
      props.options = props.options || {};
      props.options.elements = props.options.elements || {};
      props.options.elements.line = props.options.elements.line || {};
      //props.options.elements.line.tension = 0;

      return <Line {...props} options={{ ...props.options }} />;
    default:
      return <Bar {...props} />;
  }
};

class Graph extends Component {
  constructor(props) {
    super(props);
    this.state = {
      view: "line",
      suggestedMinY: 0,
      suggestedMaxY: 0,
      kpi: 0
    };
  }

  toDate(d) {
    return moment(d).endOf("day"); //format("D MMM");
    return moment(d).format("D MMM");
  }

  getAxisLabels(datasets = []) {
    const vt = this.viewType();
    let axis = [];
    //build axis
    for (let i = 0; i < datasets.length; i++) {
      const dataset = datasets[i];
      for (let j = 0; j < dataset.length; j++) {
        const datasetElement = dataset[j];
        if (datasetElement.date && axis.indexOf(datasetElement.date) === -1) {
          axis.push(this.toDate(datasetElement.date));
        }
      }
    }
    //add period range
    if (this.props.period.length >= 2) {
      const periodStart = this.props.period[0];
      const periodEnd = this.props.period[1];
      axis.push(
        moment(periodStart).startOf("day")
        //.subtract(1, unit)
      );
      if (vt === "day" || vt === "week") {
        axis.push(
          moment(periodEnd).startOf("day")
          //.add(1, unit)
        );
      }
    }

    return axis;
  }

  viewType() {
    const period = this.props.period || [];
    if (period.length < 2) return "day";
    const diff = period[1].diff(period[0], "days");

    if (diff < 30) {
      return "day";
    } else if (diff < 90) {
      return "week";
    } else {
      return "month";
    }
  }

  toDatasetData(data = []) {
    const datasetData = [];
    const { kpi } = this.props;
    for (let i = 0; i < data.length; i++) {
      const datum = data[i];
      if (datum.hasOwnProperty("date") && datum.hasOwnProperty("v")) {
        datasetData.push({
          x: moment(datum.date),
          y: StatsUtils.roundKpiValue(kpi, datum.v)
        });
      }
    }

    return datasetData;
  }

  /**
   * Check if point if the max or min value from datasets
   * @param point
   * @param otherDatasets
   * @param op
   * @return {boolean}
   */
  isExtreme(point, otherDatasets = [], op = "max") {
    if (otherDatasets.length === 0) return true;
    const { x, y } = point;
    let otherCapValue = null;
    for (let i = 0; i < otherDatasets.length; i++) {
      const { data = [] } = otherDatasets[i];
      const nearestPoint = data.reduce((acc, value) => {
        if (moment(value.x).isSameOrBefore(x)) {
          return value;
        }
        return acc;
      }, null);
      if (!nearestPoint) continue;
      if (otherCapValue === null) {
        otherCapValue = nearestPoint.y;
      } else {
        otherCapValue =
          op === "max" ? Math.max(otherCapValue, nearestPoint.y) : Math.min(otherCapValue, nearestPoint.y);
      }
    }

    otherCapValue = otherCapValue || 0;
    return op === "max" ? y >= otherCapValue : y <= otherCapValue;
  }

  getAlignPosition(context) {
    const { dataIndex = 0, dataset = {}, chart = {} } = context;
    const data = chart.data || {};
    const datasets = data.datasets || [];
    if (datasets.length === 1) {
      //return "top";
    }
    const otherDatasets = datasets.filter((v, i) => v.label !== dataset.label);
    const pt = dataset.data[dataIndex];
    const isMax = this.isExtreme(pt, otherDatasets, "max");
    const isMin = this.isExtreme(pt, otherDatasets, "min");

    if (this.state.view === "bar") {
      if (pt.y <= 0) {
        return "start";
      } else {
        return "end";
      }
    }

    return "center";
    if (isMax) {
      return "top";
    } else if (isMin) {
      return "bottom";
    } else {
      return "center";
    }
  }
  /**
   * @param color
   * @return {Object}
   */
  getDatalabelsStyle(color) {
    const view = this.state.view;
    const style = {
      borderWidth: 1,
      backgroundColor: "white",
      borderColor: color,
      color: color
    };

    if (view === "bar") {
      return {
        ...style,
        backgroundColor: "rgba(255, 255, 255, 0.6)",
        borderWidth: 0,
        borderRadius: 0
      };
    }

    return style;
  }

  buildDataset({ id = "", data = [] }, index = 0) {
    const color = idToColor(id, index);
    return {
      label: id,
      data: this.toDatasetData(data),
      fill: false,
      borderColor: color,
      backgroundColor: color,
      datalabels: {
        ...this.getDatalabelsStyle(color),
        display: (context = {}) => {
          if (this.state.view === "line") return "auto";
          //display only min/max
          const { dataIndex = 0, dataset = {}, chart = {} } = context;
          const data = chart.data || {};
          const datasets = data.datasets || [];
          const otherDatasets = datasets.filter((v, i) => v.label !== dataset.label);
          const isMax = this.isExtreme(dataset.data[dataIndex], otherDatasets, "max");
          const isMin = this.isExtreme(dataset.data[dataIndex], otherDatasets, "min");

          if (isMax || isMin) {
            return true;
          } else {
            return false;
          }
        },
        align: (context = {}) => {
          return this.getAlignPosition(context);
        },
        anchor: (context = {}) => {
          return this.getAlignPosition(context);
        }
      }
    };
  }

  getGraphData() {
    let { data } = this.props;
    const filteredData = data; //this.filterData(data);
    const graphData = {
      labels: this.getAxisLabels(filteredData),
      datasets: []
    };

    for (let i = 0; i < filteredData.length; i++) {
      const datum = filteredData[i];
      const dataset = this.buildDataset(datum, i);
      if (this.state.view === "bar") {
        //dataset.backgroundColor = gradient;
      }
      graphData.datasets.push(dataset);
    }

    return graphData;
  }

  getExtremeValues() {
    let { data } = this.props;
    let min = null;
    let max = null;
    //data = this.filterData(data);
    for (let i = 0; i < data.length; i++) {
      const dataset = data[i] || {};
      const datasetData = dataset.data || [];
      for (let j = 0; j < datasetData.length; j++) {
        const { v = null } = datasetData[j];
        if (v !== null) {
          if (min === null) {
            min = v;
            max = v;
          } else {
            min = Math.min(v, min);
            max = Math.max(v, max);
          }
        }
      }
    }

    min = min || 0;
    max = max || 0;
    return { min, max };
  }
  getGraphOptions() {
    const { kpi = {} } = this.props;
    const xValues = this.getExtremeValues();
    let suggestedMinY = xValues.min * (xValues.min > 0 ? 0.9 : 1.1);
    let suggestedMaxY = xValues.max * 1.1;

    if (kpi.format && kpi.format.toLowerCase() === "pourcentage" && suggestedMaxY > 100) {
      suggestedMaxY = 100;
    }

    if (kpi.id !== this.state.kpi) {
      //update min/max
      this.setState({
        kpi: kpi.id,
        suggestedMinY,
        suggestedMaxY
      });
    } else if (
      //preserve min/max
      suggestedMinY !== 0 &&
      suggestedMaxY !== 0 &&
      (this.state.suggestedMinY !== suggestedMinY || this.state.suggestedMaxY !== suggestedMaxY)
    ) {
      this.setState({
        kpi: kpi.id,
        suggestedMinY,
        suggestedMaxY
      });
    }
    if (kpi.format === "Pourcentage") {
      //suggestedMaxY = 100;
    }
    return {
      plugins: {
        datalabels: {
          align: "end",
          anchor: "end",
          clamping: true,
          borderRadius: 32,
          color: "white",
          font: {
            size: 10,
            weight: "bold"
          },
          formatter: function(value, context) {
            return value.y;
          }
        }
      },
      responsive: true,
      legend: {
        position: "bottom"
      },
      layout: {
        padding: { top: 40 }
      },
      scales: {
        xAxes: [
          {
            //distribution: 'series',
            offset: true,
            type: "time",
            //categoryPercentage: 1,
            //barThickness: 10,
            gridLines: {
              offsetGridLines: false,
              display: false
            },
            ticks: {
              autoSkip: false
              //scaleBeginAtZero: true,
            },
            time: {
              unit: this.viewType(),
              //unitStepSize: 1,
              tooltipFormat: this.viewType() !== "month" ? "D MMM" : "MMM",
              displayFormats: {
                day: "D MMM"
              }
            }
          }
        ],
        yAxes: [
          {
            gridLines: {
              display: false
            },
            scaleLabel: {
              display: true,
              labelString: kpi.long_label || " "
            },
            ticks: {
              //fontColor: axisColor,
              suggestedMin: this.state.suggestedMinY,
              suggestedMax: this.state.suggestedMaxY
            }
          }
        ]
      }
    };
  }

  render() {
    const data = this.getGraphData();

    return (
      <div
        style={{
          position: "relative",
          alignItems: "center",
          justifyContent: "center",
          paddingTop: 30
        }}
      >
        <div style={{ position: "absolute", top: -10, right: 20 }}>
          <Button.Group
            size="small"
            value={this.state.view}
            onClick={e => {
              this.setState({ view: e.target.value });
            }}
          >
            {/*<Button
							style={
								this.state.view === "reload"
									? {
											color: "#515151",
											borderColor: "#515151",
											zIndex: 3,
									  }
									: {
											color: "#eee",
											borderColor: "#eee",
									  }
							}
							value="line"
							icon={"reload"}
							onClick={() => {
								this.props.fetchStats();
							}}
						/>*/}
            <Button
              style={
                this.state.view === "bar"
                  ? {
                      color: "#515151",
                      borderColor: "#515151",
                      zIndex: 3,
                      marginRight: 5,
                      marginLeft: 5
                    }
                  : {
                      color: "#eee",
                      borderColor: "#eee",
                      backgroundColor: "#fff",
                      marginRight: 5,
                      marginLeft: 5
                    }
              }
              value="bar"
              icon={"bar-chart"}
            />
            <Button
              style={
                this.state.view === "line"
                  ? {
                      color: "#515151",
                      borderColor: "#515151",
                      zIndex: 3
                    }
                  : {
                      color: "#eee",
                      borderColor: "#eee",
                      backgroundColor: "#fff"
                    }
              }
              value="line"
              icon={"line-chart"}
            />
          </Button.Group>
        </div>
        <GraphWithView
          //plugins={[ChartDataLabels]}
          view={this.state.view}
          data={data}
          options={this.getGraphOptions()}
        />

        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)"
          }}
        >
          <GraphSpinner loading={this.props.graphLoading} />
        </div>
      </div>
    );
  }
}

Graph.propTypes = {};

export default Graph;



// WEBPACK FOOTER //
// src/modules/stats/components/Graph.js